import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { DatafoxDevice } from "../models/datafox-device.model";
import { FileUpload } from "../models/fileUpload.model";
import { FileUploadType } from "../models/fileUploadType.enum";
import { AlertService, MessageSeverity } from "./alert.service";
import { AuthService } from "./auth.service";
import { FileUploadEndpointService } from "./fileupload-endpoint.service";
import { FileUploadStatus } from "../models/fileUploadStatus.model";

@Injectable()
export class FileUploadService implements OnDestroy {

  public allFileUploadsChanged: BehaviorSubject<FileUpload[]> = new BehaviorSubject<FileUpload[]>(null);
  private updateAllFileUploadsChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private updateFileUploadsSubscription: Subscription;
  private logoutSubscription: Subscription;

  constructor(private fileUploadEndpointService: FileUploadEndpointService, private alertService: AlertService,
    private authService: AuthService) {

    this.logoutSubscription = this.authService.logoutEmitter.subscribe(logout => {
      if (logout == false) {
        this.updateAllFileUploads();
      }
    });

    // Aktualisierung der Shops zusammenfassen, damit nicht zwei Anfragen
    // zur fast gleichen Zeit an den Server gehen
    this.updateFileUploadsSubscription = this.updateAllFileUploadsChange
      .pipe(
        debounceTime(10),
      )
      .subscribe(res => {
        this.updateAllFileUploadsExecute();
      });
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
    if (this.updateFileUploadsSubscription) {
      this.updateFileUploadsSubscription.unsubscribe();
    }
  }

  public updateAllFileUploads() {
    this.updateAllFileUploadsChange.next(true);
  }

  public updateAllFileUploadsExecute() {
    if (this.authService.isLoggedIn) {
      this.getAllUploadFiles().then(shops => {
        this.allFileUploadsChanged.next(shops);
      }).catch(error => {

      });
    }
  }

  public get allFileUploads(): FileUpload[] {
    return this.allFileUploadsChanged.value;
  }

  public uploadSetup(file: File, devices: DatafoxDevice[], fileUploadType: FileUploadType): Promise<any> {
    return new Promise((resolve, reject) => {
      let formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('Type', '' + fileUploadType);

      let list: string = '';
      if (devices?.length) {
        for (let device of devices) {
          if (list?.length) {
            list = list + '|';
          }
          list = list + device.deviceType + ',' + device.serialNumber;
        }
      }

      if (list?.length) {
        formData.append('Devices', list);
      }
      this.fileUploadEndpointService.uploadFile(formData, false).subscribe({
        next: ok => {
          resolve(ok);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Upload ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject(error);
        }
      });
    });
  }

  public uploadCertificate(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      let formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('Type', '' + FileUploadType.TlsRootCertificate);
      this.fileUploadEndpointService.uploadFile(formData, false).subscribe({
        next: ok => {
          resolve(ok);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Upload des Zertifikats ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject(error);
        }
      });
    });
  }

  public getAllUploadFiles(): Promise<FileUpload[]> {
    return new Promise((resolve, reject) => {
      return this.fileUploadEndpointService.getAllFileUploads(false).subscribe({
        next: data => {
          resolve(data);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Laden der Uploads ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

  public getUploadFile(id: string): Promise<FileUploadStatus[]> {
    return new Promise((resolve, reject) => {
      return this.fileUploadEndpointService.get(id, false).subscribe({
        next: data => {
          resolve(data);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Laden des Uploads mit der ID ' + id + ' ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

}
